import type {
  BilingualName,
  PageSection,
  SchemaField,
  SectionType,
  SectionVariant,
  ThemeSchema,
} from '@/types/theme-workspace'

interface RawSectionVariant {
  id?: string
  variant_id?: string
  template_key?: string
  section_type_code?: string
  name?: BilingualName | string
  schema?: {
    fields?: SchemaField[]
    settings?: SchemaField[]
    style_tokens?: SchemaField[]
  }
  default_content?: Record<string, unknown>
  default_settings?: Record<string, unknown>
}

interface RawSectionType {
  code?: string
  section_type_code?: string
  name?: BilingualName | string
  mandatory?: boolean
  max_instances?: number
  default_variant_id?: string
  variants?: RawSectionVariant[]
}

interface RawPageSection {
  theme_section_id?: string
  section_id?: string
  section_type_code?: string
  section_code?: string
  section_type_name?: BilingualName | string
  page?: string
  sort_order?: number
  visible?: boolean
  variant_id?: string
  template_key?: string
  schema?: {
    fields?: SchemaField[]
    settings?: SchemaField[]
    style_tokens?: SchemaField[]
  }
  default_content?: Record<string, unknown>
  default_settings?: Record<string, unknown>
  variants?: RawSectionVariant[]
}

export interface RawThemeSchema {
  global_style?: Record<string, unknown>
  global_style_schema?: SchemaField[]
  branding_schema?: SchemaField[]
  default_section_order?: Record<string, string[]>
  mandatory_section_codes?: string[]
  sections_by_page?: Record<string, RawPageSection[]>
  section_types?: RawSectionType[]
}

const FALLBACK_PAGES = ['home', 'listing', 'product_detail', 'cart', 'checkout', 'all'] as const

function ensureBilingualName(name: BilingualName | string | undefined, fallback: string): BilingualName {
  if (!name) {
    return { ar: fallback, en: fallback }
  }

  if (typeof name === 'string') {
    return { ar: name, en: name }
  }

  return {
    ar: name.ar || name.en || fallback,
    en: name.en || name.ar || fallback,
  }
}

function normalizeSchemaField(field: SchemaField): SchemaField {
  return {
    ...field,
    item_schema: field.item_schema?.map(normalizeSchemaField),
  }
}

function normalizeSchemaBlock(
  schema: RawSectionVariant['schema'] | RawPageSection['schema'] | undefined,
) {
  return {
    fields: schema?.fields?.map(normalizeSchemaField) ?? [],
    settings: schema?.settings?.map(normalizeSchemaField) ?? [],
    style_tokens: schema?.style_tokens?.map(normalizeSchemaField) ?? [],
  }
}

function createFallbackVariant(sectionTypeCode: string, fallbackId: string): SectionVariant {
  return {
    variant_id: fallbackId,
    template_key: sectionTypeCode,
    section_type_code: sectionTypeCode,
    name: ensureBilingualName(undefined, sectionTypeCode),
    schema: normalizeSchemaBlock(undefined),
    default_content: {},
    default_settings: {},
  }
}

function resolveSectionTypeCode(sectionType: SectionType | RawSectionType, fallbackCode = 'section') {
  const rawCode = 'code' in sectionType ? sectionType.code : undefined
  return sectionType.section_type_code ?? rawCode ?? fallbackCode
}

function normalizeSectionVariant(
  variant: RawSectionVariant,
  sectionTypeCode: string,
  fallbackVariantId: string,
): SectionVariant {
  const variantId = variant.variant_id ?? variant.id ?? fallbackVariantId

  return {
    variant_id: variantId,
    template_key: variant.template_key ?? sectionTypeCode,
    section_type_code: variant.section_type_code ?? sectionTypeCode,
    name: ensureBilingualName(variant.name, variant.template_key ?? sectionTypeCode),
    schema: normalizeSchemaBlock(variant.schema),
    default_content: variant.default_content ?? {},
    default_settings: variant.default_settings ?? {},
  }
}

function normalizeSectionType(type: RawSectionType, fallbackCode: string): SectionType {
  const sectionTypeCode = type.section_type_code ?? type.code ?? fallbackCode
  const fallbackVariantId = type.default_variant_id ?? `local:${sectionTypeCode}:default`
  const variants = (type.variants ?? []).map((variant) =>
    normalizeSectionVariant(variant, sectionTypeCode, fallbackVariantId),
  )
  const safeVariants = variants.length > 0
    ? variants
    : [createFallbackVariant(sectionTypeCode, fallbackVariantId)]
  const defaultVariant = safeVariants[0]!

  return {
    section_type_code: sectionTypeCode,
    name: ensureBilingualName(type.name, sectionTypeCode),
    mandatory: Boolean(type.mandatory),
    max_instances: type.max_instances ?? 1,
    default_variant_id: type.default_variant_id ?? defaultVariant.variant_id,
    variants: safeVariants,
  }
}

function createLocalSectionId(page: string, sectionTypeCode: string, occurrence: number): string {
  return `local:${page}:${sectionTypeCode}:${occurrence}`
}

function normalizePageSection(
  section: RawPageSection,
  page: string,
  index: number,
  sectionTypes: Map<string, SectionType>,
): PageSection {
  const sectionTypeCode = section.section_type_code ?? section.section_code ?? 'section'
  const sectionType = sectionTypes.get(sectionTypeCode)
  const fallbackId = createLocalSectionId(page, sectionTypeCode, index + 1)
  const sectionId = section.theme_section_id ?? section.section_id ?? fallbackId
  const variants = section.variants?.length
    ? section.variants.map((variant) =>
        normalizeSectionVariant(
          variant,
          sectionTypeCode,
          sectionType?.default_variant_id ?? fallbackId,
        ),
      )
    : sectionType?.variants ?? [createFallbackVariant(sectionTypeCode, fallbackId)]
  const activeVariant = variants.find((variant) => variant.variant_id === section.variant_id)
    ?? variants[0]
    ?? createFallbackVariant(sectionTypeCode, fallbackId)

  return {
    theme_section_id: sectionId,
    section_id: section.section_id ?? sectionId,
    section_type_code: sectionTypeCode,
    section_code: section.section_code ?? sectionTypeCode,
    section_type_name: ensureBilingualName(
      section.section_type_name ?? sectionType?.name,
      sectionTypeCode,
    ),
    page: section.page ?? page,
    sort_order: section.sort_order ?? index,
    visible: section.visible ?? true,
    variant_id: activeVariant.variant_id,
    template_key: section.template_key ?? activeVariant.template_key,
    schema: normalizeSchemaBlock(section.schema ?? activeVariant.schema),
    default_content: section.default_content ?? activeVariant.default_content ?? {},
    default_settings: section.default_settings ?? activeVariant.default_settings ?? {},
    variants,
  }
}

function buildSectionsByPage(rawSchema: RawThemeSchema, sectionTypes: SectionType[]): Record<string, PageSection[]> {
  const sectionTypeMap = new Map(sectionTypes.map((sectionType) => [sectionType.section_type_code, sectionType]))
  const occurrenceByPage = new Map<string, number>()
  const pages = new Set<string>([
    ...FALLBACK_PAGES,
    ...Object.keys(rawSchema.default_section_order ?? {}),
  ])

  const sectionsByPage: Record<string, PageSection[]> = {}

  for (const page of pages) {
    const orderedCodes = rawSchema.default_section_order?.[page] ?? []

    sectionsByPage[page] = orderedCodes.flatMap((sectionTypeCode, index) => {
      const sectionType = sectionTypeMap.get(sectionTypeCode)
      if (!sectionType) return []

      const occurrenceKey = `${page}:${sectionTypeCode}`
      const occurrence = (occurrenceByPage.get(occurrenceKey) ?? 0) + 1
      occurrenceByPage.set(occurrenceKey, occurrence)

      const activeVariant = sectionType.variants.find(
        (variant) => variant.variant_id === sectionType.default_variant_id,
      ) ?? sectionType.variants[0] ?? createFallbackVariant(sectionTypeCode, sectionTypeCode)
      const sectionId = createLocalSectionId(page, sectionTypeCode, occurrence)

      return [{
        theme_section_id: sectionId,
        section_id: sectionId,
        section_type_code: sectionTypeCode,
        section_code: sectionTypeCode,
        section_type_name: sectionType.name,
        page,
        sort_order: index,
        visible: true,
        variant_id: activeVariant.variant_id,
        template_key: activeVariant.template_key,
        schema: activeVariant.schema,
        default_content: activeVariant.default_content ?? {},
        default_settings: activeVariant.default_settings ?? {},
        variants: sectionType.variants,
      } satisfies PageSection]
    })
  }

  return sectionsByPage
}

export function normalizeThemeSchema(schema: ThemeSchema | RawThemeSchema): ThemeSchema {
  const normalizedSectionTypes = (schema.section_types ?? []).map((sectionType) =>
    normalizeSectionType(sectionType, resolveSectionTypeCode(sectionType)),
  )
  const sectionTypeMap = new Map(
    normalizedSectionTypes.map((sectionType) => [sectionType.section_type_code, sectionType]),
  )

  const sectionsByPage = schema.sections_by_page
    ? Object.fromEntries(
        Object.entries(schema.sections_by_page).map(([page, sections]) => [
          page,
          sections.map((section: RawPageSection, index: number) =>
            normalizePageSection(section, page, index, sectionTypeMap),
          ),
        ]),
      )
    : buildSectionsByPage(schema, normalizedSectionTypes)

  return {
    global_style: schema.global_style,
    global_style_schema: (schema.global_style_schema ?? []).map(normalizeSchemaField),
    branding_schema: (schema.branding_schema ?? []).map(normalizeSchemaField),
    default_section_order: schema.default_section_order ?? {},
    mandatory_section_codes:
      schema.mandatory_section_codes
      ?? normalizedSectionTypes
        .filter((sectionType) => sectionType.mandatory)
        .map((sectionType) => sectionType.section_type_code),
    sections_by_page: sectionsByPage,
    section_types: normalizedSectionTypes,
  }
}